import { ref, computed, onBeforeUnmount } from 'vue';
// @ts-ignore - api.js no tiene declaraciones TS
import { api } from '../services/api';

/**
 * Composable para grabación de audio y transcripción vía faster-whisper.
 *
 * Usa MediaRecorder API del navegador para capturar audio del micrófono
 * y lo envía al backend para transcripción con Whisper.
 *
 * Usage:
 *   const { isRecording, isTranscribing, startRecording, stopRecording, error } = useAudioRecorder();
 */
export function useAudioRecorder() {
  const isRecording = ref(false);
  const isTranscribing = ref(false);
  const error = ref<string | null>(null);
  const recordingDuration = ref(0);

  let mediaRecorder: MediaRecorder | null = null;
  let audioChunks: Blob[] = [];
  let durationInterval: ReturnType<typeof setInterval> | null = null;
  let stream: MediaStream | null = null;

  const canRecord = computed(() => typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia);

  /**
   * Iniciar grabación de audio desde el micrófono.
   */
  const startRecording = async (): Promise<boolean> => {
    error.value = null;
    audioChunks = [];
    recordingDuration.value = 0;

    if (!canRecord.value) {
      error.value = 'Tu navegador no soporta grabación de audio';
      return false;
    }

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });

      mediaRecorder = new MediaRecorder(stream, {
        mimeType: getSupportedMimeType(),
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.start(250); // Recopilar datos cada 250ms
      isRecording.value = true;

      // Contador de duración
      durationInterval = setInterval(() => {
        recordingDuration.value++;
      }, 1000);

      return true;
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        error.value = 'Permiso de micrófono denegado. Habilita el acceso al micrófono en tu navegador.';
      } else if (err.name === 'NotFoundError') {
        error.value = 'No se encontró un micrófono en tu dispositivo.';
      } else {
        error.value = `Error al acceder al micrófono: ${err.message}`;
      }
      return false;
    }
  };

  /**
   * Detener grabación y transcribir el audio.
   * Retorna el texto transcrito o null si hay error.
   */
  const stopAndTranscribe = async (
    tenantId: string,
    language: string = 'es-CO',
  ): Promise<string | null> => {
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      return null;
    }

    // Detener grabación
    return new Promise((resolve) => {
      mediaRecorder!.onstop = async () => {
        // Limpiar intervalos y stream
        if (durationInterval) {
          clearInterval(durationInterval);
          durationInterval = null;
        }
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
          stream = null;
        }
        isRecording.value = false;

        if (audioChunks.length === 0) {
          error.value = 'No se capturó audio';
          resolve(null);
          return;
        }

        // Combinar chunks en un solo blob
        const audioBlob = new Blob(audioChunks, { type: mediaRecorder!.mimeType });
        audioChunks = [];

        // Enviar al backend para transcripción
        isTranscribing.value = true;
        error.value = null;

        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          formData.append('tenant_id', tenantId);
          formData.append('language', language);

          const response = await api.post('/stt/whisper', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000, // 60s timeout para transcripción
          });

          const text = response.data?.text || '';
          if (!text.trim()) {
            error.value = 'No se pudo transcribir el audio. Intenta hablar más claro.';
            resolve(null);
          } else {
            resolve(text.trim());
          }
        } catch (err: any) {
          const detail = err.response?.data?.detail || 'Error de conexión con el servicio de transcripción.';
          error.value = detail;
          resolve(null);
        } finally {
          isTranscribing.value = false;
        }
      };

      mediaRecorder!.stop();
    });
  };

  /**
   * Cancelar grabación sin transcribir.
   */
  const cancelRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    if (durationInterval) {
      clearInterval(durationInterval);
      durationInterval = null;
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      stream = null;
    }
    isRecording.value = false;
    audioChunks = [];
    recordingDuration.value = 0;
  };

  // Limpiar al desmontar
  onBeforeUnmount(() => {
    cancelRecording();
  });

  return {
    isRecording,
    isTranscribing,
    recordingDuration,
    error,
    canRecord,
    startRecording,
    stopAndTranscribe,
    cancelRecording,
  };
}

/**
 * Retorna el MIME type de audio soportado por el navegador.
 */
function getSupportedMimeType(): string {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return 'audio/webm';
}
